import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./SalesDashboard.css";

/* ================= API ================= */
const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://api.quote.softvolute.in";

/* ================= STATUS OPTIONS ================= */
const STATUS_OPTIONS = [
  "All",
  "New",
  "Working",
  "Meeting Done",
  "Follow-up",
  "Quote Shared",
  "Waiting Closure",
  "Closed - Won",
  "Closed - Lost",
];

export default function SalesDashboard() {
  const navigate = useNavigate();

  /* ================= STATE ================= */
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [monthFilter, setMonthFilter] = useState("");

  /* ================= LOAD LEADS ================= */
  const loadLeads = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/leads`);
      setLeads(res.data || []);
    } catch (err) {
      console.error("Error loading leads:", err);
    }
  };

  useEffect(() => {
    loadLeads();
  }, []);

  /* ================= HELPERS ================= */
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "-");

  const formatINR = (value) =>
    value || value === 0
      ? `₹ ${Number(value).toLocaleString("en-IN", {
          maximumFractionDigits: 2,
        })}`
      : "-";

  // ✅ Only approved / won value
  const getWonLeadAmount = (lead) => {
    return Number(lead.projectValue || 0);
  };

  const getLeadRelevantDate = (lead) => {
    return lead.nextFollowUpDate || lead.meetingDate || lead.createdAt;
  };

  const getWonDate = (lead) => {
    return lead.updatedAt || lead.closedAt || lead.createdAt;
  };

  /* ================= FILTERED LEADS ================= */
  const filtered = useMemo(() => {
    let data = [...leads];

    /* 🔍 SEARCH */
    if (q) {
      const s = q.toLowerCase();
      data = data.filter((l) =>
        [
          l.leadName,
          l.clientDetails?.ClientName,
          l.clientDetails?.email,
          l.clientDetails?.phoneNumber,
        ]
          .join(" ")
          .toLowerCase()
          .includes(s),
      );
    }

    /* 🏷 STATUS */
    if (statusFilter !== "All") {
      data = data.filter((l) => l.status === statusFilter);
    }

    /* 📅 MONTH FILTER */
    if (monthFilter) {
      const [year, month] = monthFilter.split("-").map(Number);

      data = data.filter((l) => {
        const d =
          l.status === "Closed - Won" ? getWonDate(l) : getLeadRelevantDate(l);

        if (!d) return false;

        const date = new Date(d);
        return date.getFullYear() === year && date.getMonth() + 1 === month;
      });
    }

    return data;
  }, [q, leads, statusFilter, monthFilter]);

  /* ================= MONTHLY SALES ACHIEVED ================= */
  const monthlySalesSummary = useMemo(() => {
    const today = new Date();

    const selectedYear = monthFilter
      ? Number(monthFilter.split("-")[0])
      : today.getFullYear();

    const selectedMonth = monthFilter
      ? Number(monthFilter.split("-")[1])
      : today.getMonth() + 1;

    const wonLeads = leads.filter((lead) => {
      if (lead.status !== "Closed - Won") return false;

      const d = getWonDate(lead);
      if (!d) return false;

      const date = new Date(d);

      return (
        date.getFullYear() === selectedYear &&
        date.getMonth() + 1 === selectedMonth
      );
    });

    const totalWonAmount = wonLeads.reduce(
      (sum, lead) => sum + getWonLeadAmount(lead),
      0,
    );

    return {
      count: wonLeads.length,
      amount: totalWonAmount,
      monthLabel: new Date(selectedYear, selectedMonth - 1).toLocaleString(
        "en-IN",
        {
          month: "long",
          year: "numeric",
        },
      ),
    };
  }, [leads, monthFilter]);

  /* ================= OVERALL STATS ================= */
  const stats = useMemo(() => {
    return {
      total: filtered.length,
      won: filtered.filter((l) => l.status === "Closed - Won").length,
      lost: filtered.filter((l) => l.status === "Closed - Lost").length,
      active: filtered.filter(
        (l) => !["Closed - Won", "Closed - Lost"].includes(l.status),
      ).length,
    };
  }, [filtered]);

  /* ================= UI ================= */
  return (
    <div className="sf-list-page">
      {/* ================= HEADER ================= */}
      <div className="sf-list-header">
        <div>
          <h2>Leads</h2>
          <span className="sf-subtitle">Active / Won / Lost – All Leads</span>
        </div>

        <div className="sf-actions">
          <input
            className="sf-search"
            placeholder="Search name, email, phone"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          <select
            className="sf-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <input
            type="month"
            className="sf-filter"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
          />

          <button
            className="sf-btn primary"
            onClick={() => navigate("/leads/new")}
          >
            New
          </button>
        </div>
      </div>

      {/* ================= SUMMARY CARDS ================= */}
      <div className="sf-summary-cards">
        <div className="sf-summary-card">
          <h4>Total Leads</h4>
          <p>{stats.total}</p>
        </div>

        <div className="sf-summary-card">
          <h4>Active Leads</h4>
          <p>{stats.active}</p>
        </div>

        <div className="sf-summary-card">
          <h4>Won Leads</h4>
          <p>{stats.won}</p>
        </div>

        <div className="sf-summary-card">
          <h4>Lost Leads</h4>
          <p>{stats.lost}</p>
        </div>

        <div className="sf-summary-card sales-achieved">
          <h4>Sales Achieved ({monthlySalesSummary.monthLabel})</h4>
          <p>{formatINR(monthlySalesSummary.amount)}</p>
          <span>{monthlySalesSummary.count} won lead(s)</span>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="sf-table-wrapper">
        <table className="sf-table">
          <thead>
            <tr>
              <th></th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Budget (₹)</th>
              <th>Project Value (₹)</th>
              <th>Meeting</th>
              <th>Follow-up</th>
              <th>Designer</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((lead) => (
              <tr key={lead._id}>
                <td>
                  <input type="checkbox" />
                </td>

                <td
                  className="sf-link"
                  onClick={() => navigate(`/leads/${lead._id}`)}
                >
                  {lead.clientDetails?.ClientName || lead.leadName}
                </td>

                <td>{lead.clientDetails?.email || "-"}</td>

                <td>{lead.clientDetails?.phoneNumber || "-"}</td>

                <td>
                  <span
                    className={`sf-status ${lead.status
                      ?.toLowerCase()
                      .replace(/ /g, "-")}`}
                  >
                    {lead.status}
                  </span>
                </td>

                <td>{formatINR(lead.clientDetails?.clientBudget)}</td>

                <td>{formatINR(lead.projectValue || 0)}</td>

                <td>{formatDate(lead.meetingDate)}</td>

                <td>{formatDate(lead.nextFollowUpDate)}</td>

                <td>{lead.assignedDesigner?.name || "-"}</td>
              </tr>
            ))}

            {!filtered.length && (
              <tr>
                <td
                  colSpan={10}
                  style={{
                    textAlign: "center",
                    padding: 20,
                  }}
                >
                  No leads found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
